import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  CalendarDays,
  Watch,
  Mountain,
  Route,
  ShieldCheck,
  Link2,
  RefreshCw,
  Loader2,
  LocateFixed,
  HeartPulse,
  Ban,
  Trophy,
  Activity,
} from "lucide-react";
import Footer from "@/components/Footer";
import { BackLink } from "@/components/PageHeader";
import AccountMenu, { SignupLink, signOutAthlete } from "@/components/AccountMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (metric, v) =>
  metric === "elevation" ? `${Math.round(v).toLocaleString()} m` : `${(v / 1000).toFixed(1)} km`;

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-5 py-3.5 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

const PERIODS = [
  { id: "weekly", label: "7 Days" },
  { id: "monthly", label: "30 Days" },
  { id: "all", label: "All Time" },
];

function StatusBadge({ status }) {
  if (status === "gps_verified")
    return (
      <span className="font-mono text-[9px] uppercase tracking-[0.15em] border border-emerald-600/40 text-emerald-600 rounded-full px-2.5 py-1 shrink-0">
        GPS Verified
      </span>
    );
  if (status === "indoor_verified")
    return (
      <span className="font-mono text-[9px] uppercase tracking-[0.15em] border border-[#FF4E00]/40 text-[#FF4E00] rounded-full px-2.5 py-1 shrink-0">
        Indoor Verified
      </span>
    );
  return (
    <span className="font-mono text-[9px] uppercase tracking-[0.15em] border border-red-600/40 text-red-600 rounded-full px-2.5 py-1 shrink-0">
      Rejected
    </span>
  );
}

function Leaderboard({ board, metric, goal, period, setPeriod, ageGroup, setAgeGroup }) {
  return (
    <div className="mt-8 border-t border-[#0D1117]/10 pt-6" data-testid="leaderboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#374151] flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-[#FF4E00]" /> Standings
        </p>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              data-testid={`leaderboard-period-${p.id}`}
              className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-all duration-300 ${
                period === p.id
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {!board?.demo && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="age-group-chips">
          {[{ id: "", label: "All Ages" }, { id: "U35", label: "Under 35" }, { id: "35-44", label: "35–44" }, { id: "45-54", label: "45–54" }, { id: "55+", label: "55+" }].map((g) => (
            <button
              key={g.id}
              onClick={() => setAgeGroup(g.id)}
              data-testid={`leaderboard-age-${g.id || "all"}`}
              className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-all duration-300 ${
                ageGroup === g.id
                  ? "bg-[#0D1117] border-[#0D1117] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      )}
      {board?.demo && (
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#FF4E00] border border-[#FF4E00]/40 rounded-full px-3 py-1 inline-block mb-4">
          Demo standings — live data appears once athletes connect
        </p>
      )}
      {!board ? (
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Loading...</p>
      ) : (
        <ul className="space-y-3">
          {board.standings.map((s, i) => (
            <li
              key={`${s.name}-${i}`}
              className={`flex items-center justify-between gap-4 rounded-xl px-3 py-2 -mx-3 transition-colors duration-300 ${
                i === 0 ? "bg-[#38E54D]/10 border border-[#38E54D]/30" : ""
              }`}
              data-testid="leaderboard-row"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span
                  className={`font-serif text-xl w-8 shrink-0 ${
                    i === 0 ? "text-[#16A34A] font-semibold" : "text-stroke-amber"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm text-[#0D1117] truncate">{s.name}</span>
                {s.age_group && (
                  <span className="hidden sm:inline font-mono text-[8px] uppercase tracking-[0.15em] text-[#6B7280] border border-[#0D1117]/10 rounded-full px-2 py-0.5 shrink-0">
                    {s.age_group === "U35" ? "U35" : s.age_group}
                  </span>
                )}
                {i === 0 && (
                  <span className="hidden sm:inline font-mono text-[8px] uppercase tracking-[0.18em] text-[#0D1117] bg-[#38E54D] rounded-full px-2.5 py-1 shrink-0">
                    Leading the pack
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">
                  {s.activities} act · {s.gps_activities} gps
                </span>
                <span className={`font-serif text-lg ${i === 0 ? "text-[#16A34A] font-semibold" : "text-[#FF4E00]"}`}>
                  {fmt(metric, s.total)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-5 h-1 rounded-full bg-[#E7E4DA] overflow-hidden">
        <div
          className="h-full bg-[#FF4E00] transition-all duration-700"
          style={{ width: `${Math.min(100, ((board?.standings[0]?.total || 0) / goal) * 100)}%` }}
        />
      </div>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#6B7280] mt-2">
        Leader progress toward {fmt(metric, goal)}
      </p>
    </div>
  );
}

function metricLine(a) {
  const RUN_TYPES = ["Run", "VirtualRun", "TrailRun", "Walk", "Hike"];
  const isRun = RUN_TYPES.includes(a.sport_type);
  const parts = [];
  if (a.distance) parts.push(`${(a.distance / 1000).toFixed(2)} km`);
  if (isRun && a.distance > 0 && a.moving_time > 0) {
    const pace = a.moving_time / 60 / (a.distance / 1000);
    parts.push(`${Math.floor(pace)}:${String(Math.round((pace % 1) * 60)).padStart(2, "0")} /km`);
  }
  if (!isRun && a.average_speed) parts.push(`${(a.average_speed * 3.6).toFixed(1)} km/h`);
  if (a.elevation) parts.push(`${Math.round(a.elevation)} m climb`);
  if (a.average_heartrate) parts.push(`${Math.round(a.average_heartrate)} bpm avg`);
  if (a.max_heartrate) parts.push(`${Math.round(a.max_heartrate)} bpm max`);
  if (a.average_cadence) parts.push(isRun ? `${Math.round(a.average_cadence * 2)} spm` : `${Math.round(a.average_cadence)} rpm`);
  if (a.calories) parts.push(`${Math.round(a.calories)} kcal`);
  return parts.join(" · ");
}

function MyEfforts({ efforts, metric }) {
  if (!efforts.length) return null;
  return (
    <div className="mt-8 border-t border-[#0D1117]/10 pt-6" data-testid="my-efforts">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#374151] flex items-center gap-2 mb-4">
        <Activity className="w-3.5 h-3.5 text-[#FF4E00]" /> My Efforts
      </p>
      <ul className="space-y-3">
        {efforts.map((a) => (
          <li key={a.provider_activity_id || a.strava_activity_id} className="flex items-center justify-between gap-4" title={a.reason}>
            <div className="min-w-0">
              <p className="text-sm text-[#0D1117] truncate">{a.name}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#6B7280] mt-0.5">
                {(a.start_date || "").slice(0, 10)} · {a.sport_type}
              </p>
              {metricLine(a) && (
                <p className="font-mono text-[10px] text-[#374151] mt-1" data-testid="effort-metrics">
                  {metricLine(a)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-serif text-base text-[#0D1117]">{fmt(metric, a.value)}</span>
              <StatusBadge status={a.status} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ChallengeCard({ c, participantId, busy, syncTick, onJoin, onConnect, onConnectGarmin, onSync, form, setForm }) {
  const Icon = c.metric === "elevation" ? Mountain : Route;
  const [period, setPeriod] = useState("all");
  const [ageGroup, setAgeGroup] = useState("");
  const [board, setBoard] = useState(null);
  const [efforts, setEfforts] = useState([]);
  const [needsRescope, setNeedsRescope] = useState(false);
  const [participantInfo, setParticipantInfo] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    axios.get(`${API}/results/${c.slug}`).then((r) => setResults(r.data)).catch(() => {});
  }, [c.slug]);

  useEffect(() => {
    axios
      .get(`${API}/challenges/${c.slug}/leaderboard`, { params: { period, age_group: ageGroup } })
      .then((r) => setBoard(r.data))
      .catch(() => setBoard({ demo: true, standings: [] }));
  }, [c.slug, period, ageGroup, syncTick]);

  useEffect(() => {
    if (!participantId) {
      setEfforts([]);
      setNeedsRescope(false);
      setParticipantInfo(null);
      return;
    }
    axios
      .get(`${API}/participants/${participantId}/activities`)
      .then((r) => setEfforts(r.data.filter((a) => a.challenge_slug === c.slug)))
      .catch(() => {});
    axios
      .get(`${API}/participants/${participantId}`)
      .then((r) => {
        setParticipantInfo(r.data);
        setNeedsRescope(r.data.strava_connected && !r.data.full_metrics);
      })
      .catch(() => {});
  }, [participantId, c.slug, syncTick]);

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="challenge-card"
      className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-7 sm:p-10"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="w-14 h-14 rounded-full border border-[#FF4E00]/30 flex items-center justify-center text-[#FF4E00]">
          <Icon className="w-6 h-6" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FBF9F5] bg-[#FF4E00] px-3 py-1.5 rounded-full">
          Open Entry
        </span>
      </div>
      <Link to={`/challenges/${c.slug}`} data-testid="challenge-title-link">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight mt-7 hover:text-[#FF4E00] transition-colors duration-300">{c.title}</h2>
      </Link>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] mt-3">
        {c.distance_label || `Goal ${fmt(c.metric, c.goal)}`}
        {c.start_date && c.end_date && (
          <span className="text-[#374151]">
            {" "}· <CalendarDays className="w-3 h-3 inline -mt-0.5" />{" "}
            {new Date(`${c.start_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            {" – "}
            {new Date(`${c.end_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </span>
        )}
      </p>
      <p className="text-sm text-[#374151] leading-relaxed mt-4">{c.description}</p>

      {c.townscript_url && (
        c.townscript_url.startsWith("/") ? (
          <Link
            to={c.townscript_url}
            data-testid="challenge-register-interest-link"
            className="group mt-6 inline-flex items-center justify-center gap-2 border border-[#FF4E00]/50 hover:border-[#FF4E00] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300 hover:bg-[#FF4E00] hover:text-[#FBF9F5]"
          >
            Register Interest
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        ) : (
          <a
            href={c.townscript_url}
            target="_blank"
            rel="noopener"
            data-testid="townscript-register-link"
            className="group mt-6 inline-flex items-center justify-center gap-2 border border-[#FF4E00]/50 hover:border-[#FF4E00] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300 hover:bg-[#FF4E00] hover:text-[#FBF9F5]"
          >
            Register Online
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
          </a>
        )
      )}

      {!participantId ? (
        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onJoin(c.slug);
          }}
        >
          <input
            required
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            data-testid="challenge-input-name"
          />
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            data-testid="challenge-input-email"
          />
          <button
            type="submit"
            disabled={busy === "join"}
            data-testid="challenge-join-button"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
          >
            {busy === "join" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join Challenge"}
          </button>
          <Link
            to="/profile"
            data-testid="join-profile-link"
            className="block text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280] hover:text-[#FF4E00] transition-colors duration-300"
          >
            Complete your race profile (t-shirt size, emergency contact) →
          </Link>
        </form>
      ) : (
        <div className="mt-8">
          {needsRescope && (
            <button
              onClick={() => onConnect(c.slug)}
              data-testid="strava-rescope-button"
              className="mb-4 w-full text-left rounded-2xl border border-amber-500/40 bg-amber-50 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-700 hover:border-amber-500 transition-colors duration-300"
            >
              Heart-rate &amp; cadence need one more Strava permission — tap to reconnect and unlock full metrics
            </button>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onConnect(c.slug)}
            disabled={busy === "connect"}
            data-testid="strava-connect-button"
            className="flex-1 inline-flex items-center justify-center gap-3 bg-[#FC4C02] hover:bg-[#ff5c14] disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-full transition-all duration-300"
          >
            {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {needsRescope ? "Reconnect Strava" : "Connect Strava"}
          </button>
          <button
            onClick={() => onSync(c.slug)}
            disabled={busy === "sync"}
            data-testid="strava-sync-button"
            className="flex-1 inline-flex items-center justify-center gap-3 border border-[#0D1117]/20 hover:border-[#FF4E00]/60 hover:text-[#FF4E00] disabled:opacity-60 text-[#0D1117] font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 rounded-full transition-all duration-300"
          >
            {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync Activities
          </button>
          <button
            type="button"
            onClick={() => onConnectGarmin(c.slug)}
            disabled={busy === "garmin"}
            data-testid="garmin-connect-button"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-[#0D1117]/15 hover:border-[#0D1117]/40 disabled:opacity-60 text-[#0D1117] font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-4 rounded-full transition-colors duration-300"
          >
            {busy === "garmin" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Watch className="w-4 h-4" />}
            {participantInfo?.garmin_connected ? "Garmin Connected" : "Connect Garmin"}
          </button>
          </div>
          {participantInfo && (
            <div className="mt-4 rounded-2xl bg-[#FBF9F5] border border-[#0D1117]/10 px-4 py-3 font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280]" data-testid="activity-sync-progress">
              <span className={participantInfo.strava_connected ? "text-[#FF4E00]" : ""}>Strava {participantInfo.strava_connected ? "connected" : "not connected"}</span>
              <span className="mx-2">·</span>
              <span className={participantInfo.garmin_connected ? "text-[#FF4E00]" : ""}>Garmin {participantInfo.garmin_connected ? "connected" : "not connected"}</span>
              {participantInfo.sync_pending > 0 && <span className="mx-2">· sync queued</span>}
              {participantInfo.sync_status?.strava?.last_sync_at && <span className="block mt-1 normal-case tracking-normal">Last Strava sync {new Date(participantInfo.sync_status.strava.last_sync_at).toLocaleString("en-IN")}</span>}
              {participantInfo.sync_status?.garmin?.last_sync_at && <span className="block mt-1 normal-case tracking-normal">Last Garmin sync {new Date(participantInfo.sync_status.garmin.last_sync_at).toLocaleString("en-IN")}</span>}
            </div>
          )}
        </div>
      )}

      <MyEfforts efforts={efforts} metric={c.metric} />
      <Leaderboard board={board} metric={c.metric} goal={c.goal} period={period} setPeriod={setPeriod} ageGroup={ageGroup} setAgeGroup={setAgeGroup} />
      {results.length > 0 && (
        <div className="mt-6 border-t border-[#0D1117]/10 pt-6" data-testid="official-results">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#374151] mb-4">Official Results</p>
          <ul className="space-y-2">
            {results.slice(0, 10).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-4 text-sm">
                <span className="text-[#0D1117]">{r.position ? `#${r.position} ` : ""}{r.name}</span>
                {r.time && <span className="font-mono text-xs text-[#FF4E00]">{r.time}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.article>
  );
}

const FAIRPLAY = [
  { icon: LocateFixed, title: "GPS required outdoors", copy: "Outdoor efforts must carry a real device-recorded GPS track — no track, no count." },
  { icon: Ban, title: "Manual entries rejected", copy: "Hand-typed distances and implausible paces are automatically thrown out." },
  { icon: HeartPulse, title: "Indoor needs sensors", copy: "Treadmill and trainer efforts count only when heart-rate or cadence data corroborates them." },
];

export function useChallengeActions() {
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState({});
  const [syncTick, setSyncTick] = useState(0);
  const [prefill, setPrefill] = useState({ name: "", email: "" });
  const [joined, setJoined] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fg_participants") || "{}");
    } catch {
      return {};
    }
  });

  // Pre-fill join forms from the signed-in athlete's race passport
  useEffect(() => {
    const token = localStorage.getItem("fg_athlete_token");
    if (!token) return;
    axios
      .get(`${API}/athlete/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPrefill({ name: r.data.name || "", email: r.data.email || "" }))
      .catch(() => {
        signOutAthlete();
      });
  }, []);

  const setBusyFor = (slug, v) => setBusy((b) => ({ ...b, [slug]: v }));

  const join = async (slug) => {
    const f = forms[slug] || prefill;
    setBusyFor(slug, "join");
    try {
      const r = await axios.post(`${API}/challenges/${slug}/register`, f);
      const next = { ...joined, [slug]: r.data.participant_id };
      setJoined(next);
      localStorage.setItem("fg_participants", JSON.stringify(next));
      toast.success(`Welcome to the arena, ${r.data.name.split(" ")[0]}. Now connect Strava.`);
    } catch {
      toast.error("Could not register — please try again");
    } finally {
      setBusyFor(slug, null);
    }
  };

  const connectGarmin = async (slug) => {
    setBusyFor(slug, "garmin");
    try {
      const r = await axios.get(`${API}/garmin/connect`, { params: { participant_id: joined[slug] } });
      if (r.data.configured && r.data.url) {
        window.location.assign(r.data.url);
      } else {
        toast.info(r.data.message || "Garmin developer access is being activated — check back shortly.");
      }
    } catch {
      toast.error("Could not start Garmin connection");
    } finally {
      setBusyFor(slug, null);
    }
  };

  const connect = async (slug) => {
    setBusyFor(slug, "connect");
    try {
      const r = await axios.get(`${API}/strava/connect`, { params: { participant_id: joined[slug] } });
      if (r.data.configured && r.data.url) {
        window.location.assign(r.data.url);
      } else {
        toast.info("Strava app keys are being set up — check back shortly.");
      }
    } catch {
      toast.error("Could not start Strava connection");
    } finally {
      setBusyFor(slug, null);
    }
  };

  const sync = async (slug) => {
    setBusyFor(slug, "sync");
    try {
      const r = await axios.post(`${API}/challenges/${slug}/sync`, { participant_id: joined[slug] });
      if (r.data.demo) {
        toast.info(r.data.message);
      } else {
        toast.success(`Synced ${r.data.synced} activities — ${r.data.verified} verified, ${r.data.rejected} rejected`);
        setSyncTick((t) => t + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusyFor(slug, null);
    }
  };

  return { forms, setForms, busy, syncTick, joined, join, connect, connectGarmin, sync, prefill };
}

const KIND_TABS = [
  { id: "all", label: "All Challenges" },
  { id: "virtual", label: "Virtual" },
  { id: "physical", label: "Physical" },
];

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [kindFilter, setKindFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const actions = useChallengeActions();

  useEffect(() => {
    axios
      .get(`${API}/challenges`)
      .then((r) => setChallenges(r.data))
      .catch(() => toast.error("Could not load challenges"));
  }, []);

  useEffect(() => {
    const strava = searchParams.get("strava");
    const garmin = searchParams.get("garmin");
    if (strava === "connected") {
      toast.success("Strava connected — hit Sync to pull in your activities");
    } else if (strava === "denied") {
      toast.error("Strava authorization was declined");
    } else if (garmin === "connected") {
      toast.success("Garmin connected — activities will sync automatically");
    } else if (garmin === "denied") {
      toast.error("Garmin authorization was declined");
    }
    if (strava || garmin) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/75 border-b border-[#0D1117]/10">
        <nav className="px-6 sm:px-12 lg:px-20 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" data-testid="challenges-brand-logo" className="group flex items-center">
            <img src="/logo-horizontal.png" alt="Fitygrity" className="h-8 sm:h-9 w-auto object-contain rounded-md transition-opacity duration-300 group-hover:opacity-80" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-6">
            <Link
              to="/gallery"
              data-testid="challenges-gallery-link"
              className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151] hover:text-[#FF4E00] transition-colors duration-300 hidden sm:inline"
            >
              Gear Room
            </Link>
            <SignupLink
              testId="header-link-signup"
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] whitespace-nowrap text-[#FF4E00] hover:text-[#E04000] transition-colors duration-300"
            />
            <AccountMenu />
            <BackLink testId="challenges-back-link" />
          </div>
        </nav>
      </header>

      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16 sm:mb-24"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <ShieldCheck className="w-4 h-4" /> Strava-Verified Competition
          </p>
          <h1
            data-testid="challenges-headline"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]"
          >
            The Virtual <span className="italic text-[#FF4E00]">Arena</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Save your spot, connect your Strava — Garmin support is on the way — and let your
            device speak for you. Every effort is verified for authenticity before it touches the
            leaderboard: distance, pace, GPS and cadence all audited.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14 sm:mb-20"
          data-testid="fairplay-strip"
        >
          {FAIRPLAY.map((f) => (
            <div key={f.title} className="rounded-2xl border border-[#0D1117]/10 bg-[#F4F2EC] p-6">
              <f.icon className="w-5 h-5 text-[#FF4E00]" />
              <h3 className="font-serif text-lg text-[#0D1117] mt-4">{f.title}</h3>
              <p className="text-sm text-[#374151] leading-relaxed mt-2">{f.copy}</p>
            </div>
          ))}
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="challenge-kind-tabs">
          {KIND_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setKindFilter(t.id)}
              data-testid={`challenge-tab-${t.id}`}
              className={`font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                kindFilter === t.id
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {challenges
            .filter((c) => kindFilter === "all" || (c.kind || "virtual") === kindFilter)
            .map((c) => (
            <ChallengeCard
              key={c.slug}
              c={c}
              participantId={actions.joined[c.slug]}
              busy={actions.busy[c.slug]}
              syncTick={actions.syncTick}
              onJoin={actions.join}
              onConnect={actions.connect}
              onConnectGarmin={actions.connectGarmin}
              onSync={actions.sync}
              form={actions.forms[c.slug] || actions.prefill}
              setForm={(f) => actions.setForms((all) => ({ ...all, [c.slug]: f }))}
            />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
